import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableAction,
  TableFilterDefinition,
} from '@shared/components/master-table/master-table.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { buildOrgUnitTreeOptions } from '@shared/utils/org-unit-tree.util';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { WorkCenterConfig, WorkCenterListResponse } from './work-center.interface';

interface CompaniesResponse {
  data?: CompaniesRecord[];
  items?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

interface OrgUnitRecord {
  orgUnitId: string;
  companiesRecordId: string;
  name: string;
  parentId?: string | null;
  type?: string | null;
  orgPath?: string | null;
  isActive?: boolean;
}

interface OrgUnitsResponse {
  data?: OrgUnitRecord[];
  items?: OrgUnitRecord[];
  results?: OrgUnitRecord[];
  orgUnits?: OrgUnitRecord[];
}

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
}

@Component({
  selector: 'app-work-center',
  standalone: true,
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule],
  templateUrl: './work-center.component.html',
  styleUrl: './work-center.component.scss',
})
export class WorkCenterComponent implements OnInit {
  dataSource = new MatTableDataSource<WorkCenterConfig>([]);
  companies: CompaniesRecord[] = [];
  orgUnits: OrgUnitRecord[] = [];
  employees: EmployeeRecord[] = [];
  isLoading = true;
  actions: TableAction<WorkCenterConfig>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<WorkCenterConfig>[] = [];
  searchValue = '';
  activeFilters: Record<string, string | number | boolean | null> = {};
  currentPage = 1;
  pageSize = 15;
  totalItems = 0;
  private loadRequestSequence = 0;

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private employeeSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  readonly scopeOptions = ['companiesRecord', 'orgUnit', 'employee'] as const;

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.configureTranslations();
      this.tableFilters = this.buildTableFilters();
      this.dataSource.data = this.dataSource.data.map((item) => this.normalizeWorkCenter(item));
    });
    void this.initializeData();
  }

  async initializeData(): Promise<void> {
    this.isLoading = true;
    try {
      await this.loadCompanies();
      await this.loadDependentLookups();
      this.tableFilters = this.buildTableFilters();
      await this.loadData();
    } finally {
      this.isLoading = false;
    }
  }

  async loadData(): Promise<void> {
    const requestId = ++this.loadRequestSequence;
    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<WorkCenterListResponse>('work-centers', {
          params: {
            page: this.currentPage,
            perPage: this.pageSize,
            ...(this.searchValue ? { search: this.searchValue } : {}),
            ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
            ...(this.getActiveScopeLevel() ? { scopeLevel: this.getActiveScopeLevel() } : {}),
            ...(this.getActiveOrgUnitId() ? { orgUnitId: this.getActiveOrgUnitId() } : {}),
            ...(this.getActiveEmployeeId() ? { employeeId: this.getActiveEmployeeId() } : {}),
            ...(this.getActiveStatus() !== '' ? { isActive: this.getActiveStatus() } : {}),
          },
        })
      );

      if (requestId !== this.loadRequestSequence) return;

      this.totalItems = response?.pagination?.total || 0;
      this.dataSource.data = (response?.data || []).map((item) => this.normalizeWorkCenter(item));
      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      if (requestId !== this.loadRequestSequence) return;
      console.error('Error loading work centers', error);
      this.dataSource.data = [];
      this.totalItems = 0;
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('workCentersCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      if (requestId === this.loadRequestSequence) this.isLoading = false;
    }
  }

  handleAdd(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/attendance/work-centers/new']);
  }

  async handleView(row: WorkCenterConfig): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/attendance/work-centers', row.workCenterId]);
  }

  async handleEdit(row: WorkCenterConfig): Promise<void> {
    if (!this.hasAccess('update')) {
      return;
    }
    await this.router.navigate(['/attendance/work-centers', row.workCenterId, 'edit']);
  }

  handleDelete(row: WorkCenterConfig): void {
    if (!this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('workCentersCatalog.dialog.deleteTitle'),
        message: this.t('workCentersCatalog.dialog.deleteMessage', { workCenter: row.name }),
        confirmLabel: this.t('workCentersCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(this.apiService.delete(`work-centers/${row.workCenterId}`));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('workCentersCatalog.notifications.deleted'), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting work center', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('workCentersCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: WorkCenterConfig[]): void {
    if (!this.hasAccess('delete') || !rows.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(rows.map((row) => firstValueFrom(this.apiService.delete(`work-centers/${row.workCenterId}`))));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('common.bulkDeleteSuccess', { count: rows.length }), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting work centers', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('workCentersCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  onSearchChange(search: string): void {
    this.searchValue = search.trim();
    this.currentPage = 1;
    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
    }
    this.searchDebounceHandle = setTimeout(() => {
      void this.loadData();
    }, 300);
  }

  async onFiltersChange(filters: Record<string, string | number | boolean | null>): Promise<void> {
    const previousCompanyId = this.getActiveCompanyId();
    const previousScopeLevel = this.getActiveScopeLevel();
    this.activeFilters = filters;

    if (previousCompanyId !== this.getActiveCompanyId() || previousScopeLevel !== this.getActiveScopeLevel()) {
      this.activeFilters['orgUnit'] = null;
      this.activeFilters['employee'] = null;
      await this.loadDependentLookups();
    }

    this.currentPage = 1;
    this.tableFilters = this.buildTableFilters();
    await this.loadData();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    void this.loadData();
  }

  onFilterSearchChange(event: { filterId: string; search: string }): void {
    if (event.filterId !== 'employee' || this.getActiveScopeLevel() !== 'employee') {
      return;
    }

    if (this.employeeSearchDebounceHandle) {
      clearTimeout(this.employeeSearchDebounceHandle);
    }

    this.employeeSearchDebounceHandle = setTimeout(() => {
      void this.loadEmployees(this.getActiveCompanyId(), event.search);
    }, 250);
  }

  private async loadCompanies(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records', {
          params: { isActive: 'true' },
        })
      );
      this.companies = this.extractCompanies(response)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
    } catch (error) {
      console.error('Error loading companies for work centers', error);
      this.companies = [];
    }
  }

  private async loadDependentLookups(): Promise<void> {
    const companyId = this.getActiveCompanyId();
    if (!companyId) {
      this.orgUnits = [];
      this.employees = [];
      return;
    }

    await Promise.all([this.loadOrgUnits(companyId), this.loadEmployees(companyId)]);
  }

  private async loadOrgUnits(companyId: string): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiService.get<OrgUnitRecord[] | OrgUnitsResponse>('org-units'));
      this.orgUnits = this.extractOrgUnits(response)
        .filter((unit) => unit.companiesRecordId === companyId && unit.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
    } catch (error) {
      console.error('Error loading org units for work centers', error);
      this.orgUnits = [];
    }
  }

  private async loadEmployees(companyId: string, search = ''): Promise<void> {
    try {
      const selectedEmployeeId = this.getActiveEmployeeId();
      const selectedEmployee = this.employees.find((employee) => employee.employeeId === selectedEmployeeId);
      const response = await firstValueFrom(
        this.apiService.get<EmployeeRecord[] | EmployeesResponse>('employees', {
          params: {
            companiesRecordId: companyId,
            isActive: 'true',
            page: 1,
            perPage: 20,
            ...(search ? { search } : {}),
          },
        })
      );
      const nextEmployees = this.extractEmployees(response)
        .filter((employee) => employee.companiesRecordId === companyId)
        .sort((left, right) => (left.fullName || '').localeCompare(right.fullName || ''));

      this.employees = selectedEmployee && !nextEmployees.some((employee) => employee.employeeId === selectedEmployee.employeeId)
        ? [selectedEmployee, ...nextEmployees]
        : nextEmployees;

      this.tableFilters = this.buildTableFilters();
    } catch (error) {
      console.error('Error loading employees for work centers', error);
      this.employees = [];
    }
  }

  private extractCompanies(response: CompaniesRecord[] | CompaniesResponse): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.companiesRecords || [];
  }

  private extractOrgUnits(response: OrgUnitRecord[] | OrgUnitsResponse): OrgUnitRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.orgUnits || [];
  }

  private extractEmployees(response: EmployeeRecord[] | EmployeesResponse): EmployeeRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.employees || [];
  }

  private normalizeWorkCenter(item: Partial<WorkCenterConfig>): WorkCenterConfig {
    const scopeLevel = this.normalizeText(item.scopeLevel) || 'orgUnit';
    const isActive = item.isActive !== false;
    const allowedZoneIds = Array.isArray(item.allowedZoneIds) ? item.allowedZoneIds.filter(Boolean) : [];
    const config = {
      requireStartGeo: item.config?.requireStartGeo === true,
      requireEndGeo: item.config?.requireEndGeo === true,
      requireStartEvidence: item.config?.requireStartEvidence === true,
      requireEndEvidence: item.config?.requireEndEvidence === true,
      validateStartZone: item.config?.validateStartZone === true,
      validateEndZone: item.config?.validateEndZone === true,
      allowOutOfZoneStart: item.config?.allowOutOfZoneStart === true,
      allowOutOfZoneEnd: item.config?.allowOutOfZoneEnd === true,
    };

    return {
      workCenterId: item.workCenterId || crypto.randomUUID(),
      center: item.center || '',
      companiesRecordId: item.companiesRecordId || '',
      name: item.name || '',
      description: item.description || '',
      address: item.address || '',
      reference: item.reference || '',
      scopeLevel,
      orgUnitId: item.orgUnitId || null,
      orgPath: item.orgPath || null,
      employeeId: item.employeeId || null,
      employeeNumber: item.employeeNumber || null,
      config,
      allowedZoneIds,
      isActive,
      companyName: this.getCompanyName(item.companiesRecordId),
      scopeLevelLabel: this.getScopeLevelLabel(scopeLevel),
      scopeTargetLabel: this.getScopeTargetLabel(scopeLevel, item.orgUnitId, item.employeeId, item.employeeNumber),
      zonesSummaryLabel: this.t('workCentersCatalog.labels.zonesCount', { count: allowedZoneIds.length }),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private buildTableFilters(): TableFilterDefinition<WorkCenterConfig>[] {
    const filters: TableFilterDefinition<WorkCenterConfig>[] = [
      {
        id: 'company',
        label: this.t('workCentersCatalog.filters.company'),
        placeholder: this.t('workCentersCatalog.filters.companyPlaceholder'),
        icon: 'business',
        searchable: true,
        value: this.activeFilters['company'] ?? null,
        options: this.companies.map((company) => ({ value: company.companiesRecordId, label: company.name })),
      },
      {
        id: 'scope',
        label: this.t('workCentersCatalog.fields.scopeLevel'),
        placeholder: this.t('workCentersCatalog.filters.scopePlaceholder'),
        icon: 'account_tree',
        searchable: false,
        value: this.activeFilters['scope'] ?? null,
        options: this.scopeOptions.map((scope) => ({ value: scope, label: this.t(`workCentersCatalog.scopeOptions.${scope}`) })),
      },
    ];

    if (this.getActiveCompanyId() && this.getActiveScopeLevel() === 'orgUnit') {
      filters.push({
        id: 'orgUnit',
        label: this.t('workCentersCatalog.fields.orgUnit'),
        placeholder: this.t('workCentersCatalog.filters.orgUnitPlaceholder'),
        icon: 'account_tree',
        type: 'tree-select',
        multiple: false,
        searchPlaceholder: this.t('workCentersCatalog.filters.orgUnitPlaceholder'),
        value: this.activeFilters['orgUnit'] ?? null,
        treeOptions: buildOrgUnitTreeOptions(this.orgUnits),
      });
    }

    if (this.getActiveCompanyId() && this.getActiveScopeLevel() === 'employee') {
      filters.push({
        id: 'employee',
        label: this.t('workCentersCatalog.fields.employee'),
        placeholder: this.t('workCentersCatalog.filters.employeePlaceholder'),
        icon: 'badge',
        searchable: true,
        value: this.activeFilters['employee'] ?? null,
        options: this.employees.map((employee) => ({
          value: employee.employeeId,
          label: `${employee.fullName || employee.employeeNumber} · ${employee.employeeNumber}`,
          searchText: `${employee.fullName || ''} ${employee.employeeNumber || ''}`.trim(),
        })),
      });
    }

    filters.push({
      id: 'status',
      label: this.t('workCentersCatalog.fields.status'),
      placeholder: this.t('workCentersCatalog.filters.statusPlaceholder'),
      icon: 'verified',
      searchable: false,
      value: this.activeFilters['status'] ?? null,
      options: [
        { value: 'true', label: this.t('common.status.active') },
        { value: 'false', label: this.t('common.status.inactive') },
      ],
    });

    return filters;
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('workCentersCatalog.actions.create'),
        colorClass: 'success',
        auth: { resource: 'workCenters', action: 'create' },
        behavior: 'add',
      },
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('common.select'),
        colorClass: 'info',
        auth: { resource: 'workCenters', action: 'read' },
        selectionMode: 'single',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('common.update'),
        colorClass: 'accent',
        auth: { resource: 'workCenters', action: 'update' },
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('common.delete'),
        colorClass: 'danger',
        auth: { resource: 'workCenters', action: 'delete' },
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'name', label: this.t('workCentersCatalog.fields.name'), type: 'text', visible: true },
      { def: 'companyName', label: this.t('orgUnitsCatalog.fields.company'), type: 'text', visible: true },
      { def: 'scopeLevelLabel', label: this.t('workCentersCatalog.fields.scopeLevel'), type: 'text', visible: true },
      { def: 'scopeTargetLabel', label: this.t('workCentersCatalog.fields.scopeTarget'), type: 'text', visible: true },
      { def: 'address', label: this.t('workCentersCatalog.fields.address'), type: 'address', visible: true },
      { def: 'zonesSummaryLabel', label: this.t('workCentersCatalog.fields.allowedZones'), type: 'text', visible: true },
      { def: 'activeLabel', label: this.t('workCentersCatalog.fields.status'), type: 'status', visible: true },
    ];

    this.tableFilters = this.buildTableFilters();
  }

  private getCompanyName(companyId: string | null | undefined): string {
    if (!companyId) {
      return this.t('workCentersCatalog.labels.noCompany');
    }
    return this.companies.find((company) => company.companiesRecordId === companyId)?.name || this.t('workCentersCatalog.labels.noCompany');
  }

  private getScopeLevelLabel(scopeLevel: string | null | undefined): string {
    const normalized = this.normalizeText(scopeLevel);
    if (!normalized) {
      return '-';
    }
    if (this.scopeOptions.includes(normalized as (typeof this.scopeOptions)[number])) {
      return this.t(`workCentersCatalog.scopeOptions.${normalized}`);
    }
    return normalized;
  }

  private getScopeTargetLabel(
    scopeLevel: string | null | undefined,
    orgUnitId: string | null | undefined,
    employeeId: string | null | undefined,
    employeeNumber: string | null | undefined
  ): string {
    const normalizedScope = this.normalizeText(scopeLevel);
    if (normalizedScope === 'companiesRecord') {
      return this.getCompanyName(this.getActiveCompanyId() || null);
    }
    if (normalizedScope === 'employee' && employeeId) {
      const employee = this.employees.find((item) => item.employeeId === employeeId);
      return employee ? `${employee.fullName || employee.employeeNumber} · ${employee.employeeNumber}` : employeeNumber || employeeId;
    }
    if (normalizedScope === 'orgUnit' && orgUnitId) {
      return this.orgUnits.find((item) => item.orgUnitId === orgUnitId)?.name || orgUnitId;
    }
    return '-';
  }

  private getActiveCompanyId(): string {
    return (this.activeFilters['company'] as string) || '';
  }

  private getActiveScopeLevel(): string {
    return (this.activeFilters['scope'] as string) || '';
  }

  private getActiveOrgUnitId(): string {
    return (this.activeFilters['orgUnit'] as string) || '';
  }

  private getActiveEmployeeId(): string {
    return (this.activeFilters['employee'] as string) || '';
  }

  private getActiveStatus(): string {
    const value = this.activeFilters['status'];
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return String(value);
  }

  private normalizeText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private hasAccess(action: string): boolean {
    return this.authorizationService.hasAccess({ resource: 'workCenters', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2200,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
